/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ParticipantType } from '../../types';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(true);

  // Keep menu open when on home page
  useEffect(() => {
    if (location.pathname === '/') {
      setMenuOpen(true);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const getMenuItems = () => {
    const items = [
      { path: '/', label: 'Home', icon: '🏠' },
      { path: '/bids', label: 'Bids', icon: '📋' },
      { path: '/components', label: 'Components', icon: '📄' },
      { path: '/calendar', label: 'Company Calendar', icon: '📅' },
    ];

    if (currentUser?.type === ParticipantType.ADMIN) {
      items.push({ path: '/participants', label: 'Participants', icon: '👥' });
      items.push({ path: '/participants/create', label: 'Create/Edit User', icon: '➕' });
      items.push({ path: '/companies', label: 'Manage Companies', icon: '🏢' });
      items.push({ path: '/companies/create', label: 'Create/Edit Company', icon: '➕' });
    }

    if (currentUser?.type === ParticipantType.MANAGER) {
      items.push({ path: '/participants/user/create', label: 'Create/Edit User', icon: '➕' });
    }

    if (currentUser?.type === ParticipantType.ADMIN) {
      items.push({ path: '/reports/activity', label: 'Activity Report', icon: '📊' });
    }

    if (currentUser?.type === ParticipantType.MANAGER || currentUser?.type === ParticipantType.ADMIN) {
      items.push({ path: '/reports/team', label: 'Team Report', icon: '👥' });
    }

    if (currentUser?.type === ParticipantType.EXECUTIVE) {
      items.push({ path: '/executive/report', label: 'Executive Report', icon: '📊' });
    }

    // Change Password is available to all users
    items.push({ path: '/change-password', label: 'Change Password', icon: '🔐' });

    return items;
  };

  return (
    <div className="layout">
      <nav className="navbar">
        <div className="nav-left">
          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className="nav-brand">
            <Link to="/" onClick={closeMenu}>Bid Generator</Link>
          </div>
        </div>
        <div className="nav-user">
          <span className="user-info">
            {currentUser?.firstName} {currentUser?.lastName}
            <span className="user-role">({currentUser?.type})</span>
          </span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </nav>

      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>Menu</h3>
          <button className="close-menu" onClick={closeMenu}>×</button>
        </div>
        <nav className="sidebar-nav">
          {getMenuItems().map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="sidebar-link"
              onClick={closeMenu}
            >
              <span className="sidebar-icon">{item.icon}</span>
              <span className="sidebar-label">{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>

      <main className={`main-content ${menuOpen ? 'shifted' : ''}`}>
        {children}
      </main>

      <footer className={`app-footer ${menuOpen ? 'shifted' : ''}`}>
        <p>© 2024-2026 symecode.com. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Layout;
